"use client";
import { useState, useEffect, useRef } from "react";
import {
  Drawer,
  IconButton,
  Box,
  CircularProgress,
  Tooltip,
  Collapse,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronDown,
  faChevronUp,
  faCircleCheck,
  faExclamationTriangle,
  faRotateRight,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { VeTypography } from "../../mui";
import { limiter } from "../../hoc";
// import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// import CloseIcon from "@mui/icons-material/Close";

interface FileUpload {
  fileName: string;
  progress: number;
  status: string;
  fileType: string;
}

interface UploadProgressDrawerProps {
  open: boolean;
  onClose: () => void;
  files: FileUpload[];
  //   getFileIcon : (fileType: string) => string;
  getFileIcon: any;
  fileStatuses: any;
  handleAbortSingleFile?: any;
  isUploading: boolean;
}

export const UploadProgressDrawer = ({
  open,
  onClose,
  files,
  getFileIcon,
  fileStatuses,
  handleAbortSingleFile,
  isUploading,
}: UploadProgressDrawerProps) => {
  const [minimized, setMinimized] = useState(false);
  // const isUploading = files.some((file) => file?.status === "uploading");
  const pendingFilesCountRef = useRef(0);

  // Auto un-minimize when new files are detected in pending status
  useEffect(() => {
    if (!fileStatuses) return;

    const currentPendingCount = Object.values(fileStatuses).filter(
      (status: any) => status?.status === "pending"
    ).length;

    // If we detect new pending files and drawer is minimized, un-minimize it
    if (currentPendingCount > pendingFilesCountRef.current && minimized) {
      setMinimized(false);
    }

    pendingFilesCountRef.current = currentPendingCount;
  }, [fileStatuses, minimized]);

  return (
    <Drawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      variant="persistent"
      transitionDuration={300}
      sx={{
        "& .MuiDrawer-paper": {
          width: 320,
          position: "fixed",
          right: 24,
          bottom: 0,
          borderRadius: "12px 12px 0 0",
          boxShadow: 2,
          border: "none",
          left: "unset",
        },
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ backgroundColor: "#F7F7F7", padding: "12px 16px" }}
      >
        <VeTypography variant="label1StrongSecondary">
          {isUploading ? "Uploading Items" : "Upload Complete"}
        </VeTypography>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap="10px"
        >
          <IconButton
            onClick={() => setMinimized(!minimized)}
            style={{ width: "24px", height: "24px" }}
          >
            <FontAwesomeIcon
              icon={minimized ? faChevronUp : faChevronDown}
              size="xs"
              style={{ color: "#5f6368", width: "12px" }}
            />
          </IconButton>
          <IconButton
            onClick={onClose}
            style={{ width: "24px", height: "24px" }}
          >
            <FontAwesomeIcon
              icon={faTimes}
              size="xs"
              style={{ color: "#5f6368", width: "12px" }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* Content - Show only if not minimized */}
      {/* {!minimized && ( */}
      <Collapse in={!minimized} timeout="auto" unmountOnExit>
        <Box sx={{ maxHeight: "375px", overflowY: "auto" }}>
          {files.map((file, index) => {
            const isError =
              fileStatuses?.[file.fileName]?.status === "error" ||
              fileStatuses?.[file.fileName]?.status === "aborted";
            return (
              <Box
                key={index}
                display="flex"
                alignItems={isError ? "flex-start" : "center"}
                justifyContent="space-between"
                gap="10px"
                sx={{
                  padding: "12px 16px",
                  opacity: isError ? 0.7 : 1,
                }}
              >
                {getFileIcon(file.fileType)}
                <Box display={"flex"} flexDirection={"column"}>
                  <Tooltip title={file.fileName}>
                    <VeTypography variant="label3StrongSecondary">
                      {limiter({ strings: file.fileName, limit: 25 })}
                    </VeTypography>
                  </Tooltip>
                  {fileStatuses?.[file.fileName]?.status === "error" && (
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      paddingTop={"2px"}
                    >
                      <FontAwesomeIcon
                        icon={faExclamationTriangle}
                        style={{
                          color: "#DE1135",
                          width: "10px",
                          paddingRight: "5px",
                        }}
                      />
                      <VeTypography variant="label4ErrorPrimary">
                        {fileStatuses?.[file.fileName]?.message ||
                          "File Upload fail."}
                      </VeTypography>
                    </Box>
                  )}
                </Box>
                <Box sx={{ marginLeft: "auto" }}>
                  {
                    fileStatuses?.[file.fileName]?.status === "pending" ? (
                      <Box
                        width="24px"
                        display={"flex"}
                        alignItems={"center"}
                        justifyContent={"center"}
                        position="relative"
                        sx={{
                          "&:hover .abort-button": {
                            display: "flex !important",
                            zIndex: 2,
                          },
                          "&:hover .progress": {
                            position: "absolute",
                            display: "none",
                          },
                        }}
                      >
                        <CircularProgress
                          className="progress"
                          size="18px"
                          sx={{
                            color: "var(--vc-violet)",
                            transition: "opacity 0.2s",
                          }}
                        />
                        <IconButton
                          className="abort-button"
                          onClick={() => {
                            handleAbortSingleFile(file.fileName);
                          }}
                          style={{
                            width: "18px",
                            height: "18px",
                            display: "none",
                          }}
                        >
                          <FontAwesomeIcon
                            icon={faTimes}
                            style={{ color: "#5f6368", width: "12px" }}
                          />
                        </IconButton>
                      </Box>
                    ) : fileStatuses?.[file.fileName]?.status === "success" ? (
                      <FontAwesomeIcon
                        icon={faCircleCheck}
                        style={{ color: "#0E8345", width: "24px" }}
                      />
                    ) : (
                      fileStatuses?.[file.fileName]?.status === "aborted" && (
                        <VeTypography variant="label3StrongDisabled">
                          Cancelled
                        </VeTypography>
                      )
                    )

                    // : (
                    //   <IconButton
                    //     onClick={() => {
                    //       handleAbortSingleFile(file.fileName);
                    //     }}
                    //     style={{ width: "24px", height: "24px" }}
                    //   >
                    //     <FontAwesomeIcon
                    //       icon={faTimes}
                    //       style={{ color: "#5f6368", width: "12px" }}
                    //     />
                    //   </IconButton>
                    // )
                  }
                </Box>
              </Box>
            );
          })}
        </Box>
      </Collapse>
      {/* )} */}
    </Drawer>
  );
};
